import { Download, Loader2, Share } from 'lucide-react'

import { Button } from '@modules/shadcn/ui/Button/Button'
import { CheckBox } from '@modules/shadcn/ui/CheckBox/CheckBox'

interface SimulationControlBarProps {
  title: string
  isPreviewMode: boolean
  isOriginalHidden: boolean
  onOriginalHiddenChange: (hidden: boolean) => void
  isVerticalLayout: boolean
  onVerticalLayoutChange: (vertical: boolean) => void
  canDownload: boolean
  isDownloading: boolean
  onDownload?: () => void
  /** サマリ付き画像 DL の有効/無効（結果画像と parameters が揃っている時のみ true） */
  canDownloadSummary: boolean
  isSummaryDownloading: boolean
  onDownloadSummary?: () => void
  canShare: boolean
  /** 共有ボタンを表示するか（falseでボタン自体を非表示） */
  showShareButton?: boolean
  onShare?: () => void
  onToggleMode: () => void
  idPrefix?: string
}

export const SimulationControlBar: React.FC<SimulationControlBarProps> = ({
  title,
  isPreviewMode,
  isOriginalHidden,
  onOriginalHiddenChange,
  isVerticalLayout,
  onVerticalLayoutChange,
  canDownload,
  isDownloading,
  onDownload,
  canDownloadSummary,
  isSummaryDownloading,
  onDownloadSummary,
  canShare,
  showShareButton = true,
  onShare,
  onToggleMode,
  idPrefix = 'simulation-control',
}) => {
  const hideOriginalId = `${idPrefix}-hide-original`
  const layoutToggleId = `${idPrefix}-vertical-layout`

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <p className="text-sm text-slate-900">{title}</p>
      <div className="flex flex-wrap items-center gap-2">
        {isPreviewMode ? (
          <>
            <label
              className="flex items-center gap-2 rounded border border-slate-200 bg-white/80 px-3 py-2 text-sm text-slate-900 shadow-sm"
              htmlFor={hideOriginalId}
            >
              <CheckBox
                checked={isOriginalHidden}
                id={hideOriginalId}
                onCheckedChange={checked => onOriginalHiddenChange(Boolean(checked))}
              />
              現況画像を非表示
            </label>
            <label
              className="flex items-center gap-2 rounded border border-slate-200 bg-white/80 px-3 py-2 text-sm text-slate-900 shadow-sm"
              htmlFor={layoutToggleId}
            >
              <CheckBox
                checked={isVerticalLayout}
                id={layoutToggleId}
                onCheckedChange={checked => onVerticalLayoutChange(Boolean(checked))}
              />
              並び変更
            </label>
          </>
        ) : null}
        <Button
          className="gap-2"
          disabled={!canDownload || isDownloading}
          type="button"
          variant="outline"
          onClick={onDownload}
        >
          {isDownloading ? (
            <Loader2 aria-hidden className="w-4 h-4 animate-spin" />
          ) : (
            <Download aria-hidden className="w-4 h-4" />
          )}
          画像のみDL
        </Button>
        <Button
          className="gap-2"
          disabled={!canDownloadSummary || isSummaryDownloading}
          type="button"
          variant="outline"
          onClick={onDownloadSummary}
        >
          {isSummaryDownloading ? (
            <Loader2 aria-hidden className="w-4 h-4 animate-spin" />
          ) : (
            <Download aria-hidden className="w-4 h-4" />
          )}
          変換情報付きDL
        </Button>
        {showShareButton && (
          <Button
            className="gap-2"
            disabled={!canShare}
            onClick={onShare}
            type="button"
            variant="outline"
          >
            <Share aria-hidden className="w-4 h-4" />
            共有
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onToggleMode}>
          {isPreviewMode ? '編集に切り替える' : 'プレビューに切り替える'}
        </Button>
      </div>
    </div>
  )
}
